import React, { lazy, Suspense } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import LoginPage from './components/LoginPage';
import AuthCallback from './components/AuthCallback';
import { useThemeMode } from './contexts/ThemeContext';
import { useAuth, usePermissions } from './contexts/AuthContext';
import { P, ADMIN_SURFACE_PERMISSIONS } from './utils/permissions';
import { NavigationGuardProvider } from './contexts/NavigationGuardContext';
import { Shell } from './layout/Shell';
import { PageSkeleton } from './components/shared/PageSkeleton';
import { RouteErrorBoundary } from './components/shared/RouteErrorBoundary';
import { PROJECT_SETTINGS_URL, settingsUrlFor } from './components/project-settings/legacyAdminLinks';
import { buildProjectSettingsAccess } from './components/project-settings/sectionRegistry';
import { ProfitabilityRedirect } from './components/reports/legacyReportLinks';

// Every page but the dashboard (the first screen) and the sign-in screens loads
// when its route opens: charts and the Reports tabs, React Flow on the planning
// board, the assistant's markdown stack, the Admin screens, the timesheet grids
// and the Projects and People areas with their data grids. `scripts/check-bundle-split.mjs`
// holds the entry's graph to its size budget.
const PlanningBoard = lazy(() => import('./components/PlanningBoard'));
const Reports = lazy(() => import('./components/reports/ScreenReports'));
const Admin = lazy(() => import('./components/Admin'));
const Timesheets = lazy(() => import('./components/Timesheets'));
const AiAssistantPage = lazy(() => import('./components/ai-assistant/AiAssistantPage'));
const PeopleManagement = lazy(() => import('./components/PeopleManagement'));
const ProjectsManagement = lazy(() => import('./components/ProjectsManagement'));
const ProjectDetailPage = lazy(() => import('./components/projects-management/ProjectDetailPage'));
const ProjectTimeline = lazy(() => import('./components/ProjectTimeline'));
const ConsultantTimeline = lazy(() => import('./components/ConsultantTimeline'));
const Settings = lazy(() => import('./components/Settings'));
const TeamAbsencesPage = lazy(() => import('./components/team-absences/TeamAbsencesPage'));
const DirectoryPage = lazy(() => import('./components/directory/DirectoryPage'));
const AboutPage = lazy(() => import('./components/about/AboutPage'));

/**
 * Redirects to /dashboard unless the user holds the required permission — either
 * a single `permission`, or **any** of `anyOf` (used by surfaces like Admin that
 * multiple distinct permissions can unlock).
 */
function PermissionGuard({
  permission,
  anyOf,
  children,
}: {
  permission?: string;
  anyOf?: string[];
  children: React.ReactNode;
}) {
  const { hasPermission, hasAnyPermission } = usePermissions();
  const ok = anyOf ? hasAnyPermission(...anyOf) : permission ? hasPermission(permission) : false;
  return ok ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

/**
 * `/admin`, with the project lists' old links rewritten first. This must run
 * before `PermissionGuard`: the guard sends a caller it refuses to the
 * dashboard, and a `manage:metadata` holder can no longer open Admin at all.
 * A caller Admin refuses who can use a Settings section lands there instead.
 * Either way `/projects` applies its own gate, so the redirect widens nothing.
 */
function AdminEntry() {
  const [searchParams] = useSearchParams();
  const { hasPermission, hasAnyPermission } = usePermissions();

  const moved = settingsUrlFor(searchParams.get('tab'));
  if (moved) return <Navigate to={moved} replace />;

  if (
    !hasAnyPermission(...ADMIN_SURFACE_PERMISSIONS) &&
    hasPermission(P.VIEW_PROJECTS) &&
    !buildProjectSettingsAccess(hasPermission).tabLocked
  ) {
    return <Navigate to={PROJECT_SETTINGS_URL} replace />;
  }

  return (
    <PermissionGuard anyOf={ADMIN_SURFACE_PERMISSIONS}>
      <Admin />
    </PermissionGuard>
  );
}

function App() {
  return (
    <NavigationGuardProvider>
      <Router>
        <AppContent />
      </Router>
    </NavigationGuardProvider>
  );
}

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const { isDarkMode } = useThemeMode();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.default',
          backgroundImage: isDarkMode
            ? 'radial-gradient(ellipse at center, #0F1419 0%, #0A0E13 100%)'
            : 'radial-gradient(ellipse at center, #ffffff 0%, #f5f5f5 100%)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Loading Teamlane...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  if (!isAuthenticated) {
    return <LoginPage ownerSignIn={location.pathname === '/login/owner'} />;
  }

  return (
    <Shell>
      <RouteErrorBoundary resetKey={location.pathname}>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/planning" element={
              <PermissionGuard permission={P.MANAGE_ALLOCATIONS}>
                <PlanningBoard />
              </PermissionGuard>
            } />
            <Route path="/people" element={
              <PermissionGuard permission={P.MANAGE_USERS}>
                <PeopleManagement />
              </PermissionGuard>
            } />
            {/* VIEW_PROJECTS is held by EMPLOYEE and above — intentionally no redirect */}
            <Route path="/projects" element={
              <ProfitabilityRedirect>
                <PermissionGuard permission={P.VIEW_PROJECTS}>
                  <ProjectsManagement />
                </PermissionGuard>
              </ProfitabilityRedirect>
            } />
            {/* Feature 12 — full-page Project Detail (consolidates the v1 modals). */}
            <Route path="/projects/:id" element={
              <PermissionGuard permission={P.VIEW_PROJECTS}>
                <ProjectDetailPage />
              </PermissionGuard>
            } />
            <Route path="/project-timeline" element={<ProjectTimeline />} />
            <Route path="/consultant-timeline" element={<ConsultantTimeline />} />
            {/* Feature 05 — Directory: always-visible people finder, no permission gate. */}
            <Route path="/directory" element={<DirectoryPage />} />
            <Route path="/timesheets" element={<Timesheets />} />
            {/* Personal preferences — deliberately ungated: every authenticated
                user has settings of their own, whatever their permissions. */}
            <Route path="/settings" element={<Settings />} />
            {/* Ungated for the same reason: which build this is, and its terms,
                are every signed-in user's to read. The route gates nothing: the
                version and release notes ship in the public bundle. */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/team-absences" element={<TeamAbsencesPage />} />
            <Route path="/assistant" element={
              <PermissionGuard permission={P.USE_AI_ASSISTANT}>
                <AiAssistantPage />
              </PermissionGuard>
            } />
            <Route path="/reports" element={
              <PermissionGuard permission={P.VIEW_REPORTS_OPERATIONAL}>
                <Reports />
              </PermissionGuard>
            } />
            <Route path="/admin" element={<AdminEntry />} />
            {/* Legacy direct route — re-home to Admin panel People & Roles tab (F14-12) */}
            <Route path="/admin/people-mappings" element={<Navigate to="/admin?tab=people" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </Shell>
  );
}

export default App;
